import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useFeatureFlag } from 'featureFlags';
import { useAppSelector } from 'hooks';

type Preferences = {
  available: boolean;
  reduceAnimations: boolean;
  ready: boolean;
  busy: boolean;
  error: string;
  setReduceAnimations: (value: boolean) => void;
  retry: () => void;
};
const defaults: Preferences = {
  available: false,
  reduceAnimations: false,
  ready: false,
  busy: false,
  error: '',
  setReduceAnimations: () => {},
  retry: () => {}
};
export const AnimationPreferencesContext = createContext<Preferences>(defaults);
export const useAnimationPreferences = () => useContext(AnimationPreferencesContext);

// A background refresh only picks up a change made on another device, so once
// a load succeeds, focus/visibility/online events re-check at most this often.
export const REFRESH_INTERVAL_MS = 60_000;
const REQUEST_TIMEOUT_MS = 10_000;
const LOAD_ERROR = 'Could not load animation settings. Try again.';
const SAVE_ERROR = 'Could not save your change. Try again.';

type State = { identity: string; value: boolean; ready: boolean; busy: boolean; error: string };
const initialState = (identity: string): State => ({
  identity,
  value: false,
  ready: false,
  busy: false,
  error: ''
});
type Outcome = { current: boolean; saved?: boolean };
/** Loads (no value) or saves (a value) the account preference; resolves to the stored value. */
export type PreferencesRequest = (signal: AbortSignal, value?: boolean) => Promise<boolean>;

/**
 * `guestSession` is the token-backed guest state (`useGuest`); Redux `guest`
 * alone reads true for every member until the profile hydrates and keeps the
 * previous player after a session ends. The app root passes it, and the
 * `request` that talks to player-api, so this module (imported by every
 * animated component) never loads the auth module and, through it, the store.
 */
export function AnimationPreferencesProvider({
  children,
  request,
  guestSession = false
}: PropsWithChildren<{ request: PreferencesRequest; guestSession?: boolean }>) {
  const enabled = useFeatureFlag('reduceAnimations');
  const guid = useAppSelector((s) => s.user.playerGUID);
  const guest = useAppSelector((s) => s.user.guest);
  const identity = enabled && guid && !guest && !guestSession ? guid : '';
  const [state, setState] = useState<State>(() => initialState(''));
  const save = useRef<(value: boolean) => void>(() => {});
  const retryLast = useRef<() => void>(() => {});

  useEffect(() => {
    setState(initialState(identity));
    if (!identity) return;
    let active = true;
    let inFlight: AbortController | undefined;
    let latest = 0;
    let loadedAt = 0;
    // A failed write survives refreshes until it is saved or the account changes.
    let failedValue: boolean | undefined;

    // Only the newest request may update state; a newer one aborts the older.
    const send = async (value?: boolean): Promise<Outcome> => {
      const id = ++latest;
      inFlight?.abort();
      const controller = new AbortController();
      inFlight = controller;
      const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const saved = await request(controller.signal, value);
        return { current: active && id === latest, saved };
      } catch {
        return { current: active && id === latest };
      } finally {
        clearTimeout(timeout);
        if (inFlight === controller) inFlight = undefined;
      }
    };

    // Loads never mark the toggle busy, and they leave state untouched when
    // nothing changed so consumers don't re-render on every refocus.
    const load = async () => {
      const { current, saved } = await send();
      if (!current) return;
      if (saved === undefined) {
        setState((s) => (s.ready || s.error === LOAD_ERROR ? s : { ...s, error: LOAD_ERROR }));
        return;
      }
      loadedAt = Date.now();
      const error = failedValue === undefined ? '' : SAVE_ERROR;
      setState((s) =>
        s.ready && s.value === saved && s.error === error
          ? s
          : { ...s, value: saved, ready: true, error }
      );
    };

    const write = async (value: boolean) => {
      setState((s) => ({ ...s, busy: true, error: '' }));
      const { current, saved } = await send(value);
      if (!current) return;
      if (saved === undefined) {
        failedValue = value;
        setState((s) => ({ ...s, busy: false, error: SAVE_ERROR }));
        return;
      }
      failedValue = undefined;
      loadedAt = Date.now();
      setState((s) => ({ ...s, value: saved, ready: true, busy: false, error: '' }));
    };

    save.current = (value) => void write(value);
    retryLast.current = () => void (failedValue === undefined ? load() : write(failedValue));
    void load();

    const refresh = () => {
      if (document.visibilityState === 'hidden' || inFlight) return;
      if (loadedAt && Date.now() - loadedAt < REFRESH_INTERVAL_MS) return;
      void load();
    };
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      active = false;
      inFlight?.abort();
      save.current = () => {};
      retryLast.current = () => {};
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [identity, request]);

  const current = !!identity && state.identity === identity;
  const ready = current && state.ready;
  // Until a signed-in player's saved choice loads, assume they want reduced
  // motion: an animation that starts now cannot be taken back once it loads.
  const reduceAnimations = !!identity && (!ready || state.value);
  const setReduceAnimations = useCallback(
    (value: boolean) => {
      if (ready) save.current(value);
    },
    [ready]
  );
  const retry = useCallback(() => retryLast.current(), []);
  const value = useMemo<Preferences>(
    () => ({
      available: !!identity,
      reduceAnimations,
      ready,
      busy: current && state.busy,
      error: current ? state.error : '',
      setReduceAnimations,
      retry
    }),
    [
      identity,
      ready,
      reduceAnimations,
      current,
      state.busy,
      state.error,
      setReduceAnimations,
      retry
    ]
  );

  return (
    <AnimationPreferencesContext.Provider value={value}>
      {children}
    </AnimationPreferencesContext.Provider>
  );
}
